import React from 'react';
import { X, AlertCircle, CheckCircle, Clock, Plane, Moon } from 'lucide-react';

const FAR117DetailModal = ({ open, onClose, dutyPeriod, rotation }) => {
  if (!open) return null;

  // Calculate detailed FAR 117 metrics
  const calculateDetailedMetrics = () => {
    if (!dutyPeriod) {
      return {
        macro: { rolling7: 0, rolling28: 0 },
        meso: { maxFDP: 780, currentDuty: 0, maxFlight: 540, currentFlight: 0 },
        micro: [],
        rest: { before: 0, after: 0 }
      };
    }

    // MACRO - Rolling limits (mock data for now)
    const macro = {
      rolling7: { max: 3600, current: 2535, remaining: 1065 },
      rolling28: { max: 11400, current: 9390, remaining: 2010 }
    };

    // MESO - Current FDP
    const now = new Date();
    const reportTime = new Date(dutyPeriod.report);
    const currentDutyMinutes = Math.max(0, (now - reportTime) / (1000 * 60));
    
    let currentFlightMinutes = 0;
    if (dutyPeriod.legs) {
      dutyPeriod.legs.forEach(leg => {
        if (leg.actual_block_minutes) {
          currentFlightMinutes += leg.actual_block_minutes;
        }
      });
    }

    const meso = {
      maxFDP: 780, // 13:00 (should be calculated based on report time and sectors)
      currentDuty: currentDutyMinutes,
      maxFlight: 540, // 9:00
      currentFlight: currentFlightMinutes,
      extensionAvailable: 120 // +2:00
    };

    // MICRO - Individual legs
    const micro = dutyPeriod.legs?.map(leg => ({
      from: leg.departure,
      to: leg.arrival,
      flightNumber: leg.flight_number,
      blockMinutes: leg.actual_block_minutes || leg.sched_block_minutes,
      status: leg.actual_block_minutes ? 'completed' : 'scheduled'
    })) || [];

    // REST requirements
    const rest = {
      before: { required: 600, actual: 690 }, // 10:00 required, 11:30 actual
      after: { required: 600, projected: 720 } // 10:00 required, 12:00 projected
    };

    // CIRCADIAN
    const reportHour = reportTime.getUTCHours();
    const isCircadianLow = reportHour >= 2 && reportHour <= 6;

    return { macro, meso, micro, rest, circadian: { isCircadianLow, reportHour } };
  };

  const metrics = calculateDetailedMetrics();

  const formatTime = (minutes) => {
    const hrs = Math.floor(minutes / 60);
    const mins = Math.floor(minutes % 60);
    return `${hrs}:${mins.toString().padStart(2, '0')}`;
  };

  const getStatusIcon = (current, max) => {
    const percent = (current / max) * 100;
    if (percent < 75) return <CheckCircle className="w-4 h-4 text-green-500" />;
    if (percent < 90) return <AlertCircle className="w-4 h-4 text-yellow-500" />;
    return <AlertCircle className="w-4 h-4 text-red-500" />;
  };

  const getPercentColor = (percent) => {
    if (percent < 75) return 'text-green-500';
    if (percent < 90) return 'text-yellow-500';
    return 'text-red-500';
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-slate-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <h2 className="text-xl font-bold text-white">FAR 117 Detailed Analysis</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors p-1 hover:bg-white/10 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* MACRO - Rotation Level */}
          <div className="space-y-3">
            <h3 className="text-sm font-bold text-gray-300 uppercase tracking-wider flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Macro - Rotation Level
            </h3>
            <div className="space-y-2 pl-6">
              {/* 7-Day Rolling */}
              <div className="bg-white/5 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-400">7-Day Rolling Limit:</span>
                  <span className="text-sm font-semibold text-gray-300">{formatTime(metrics.macro.rolling7.max)} max</span>
                </div>
                <div className="flex items-center gap-2">
                  {getStatusIcon(metrics.macro.rolling7.current, metrics.macro.rolling7.max)}
                  <span className="text-sm">
                    Current: <span className={`font-bold ${getPercentColor((metrics.macro.rolling7.current / metrics.macro.rolling7.max) * 100)}`}>
                      {formatTime(metrics.macro.rolling7.current)}
                    </span>
                    {' '}({Math.round((metrics.macro.rolling7.current / metrics.macro.rolling7.max) * 100)}% used, {formatTime(metrics.macro.rolling7.remaining)} remaining)
                  </span>
                </div>
              </div>

              {/* 28-Day Rolling */}
              <div className="bg-white/5 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-400">28-Day Rolling Limit:</span>
                  <span className="text-sm font-semibold text-gray-300">{formatTime(metrics.macro.rolling28.max)} max</span>
                </div>
                <div className="flex items-center gap-2">
                  {getStatusIcon(metrics.macro.rolling28.current, metrics.macro.rolling28.max)}
                  <span className="text-sm">
                    Current: <span className={`font-bold ${getPercentColor((metrics.macro.rolling28.current / metrics.macro.rolling28.max) * 100)}`}>
                      {formatTime(metrics.macro.rolling28.current)}
                    </span>
                    {' '}({Math.round((metrics.macro.rolling28.current / metrics.macro.rolling28.max) * 100)}% used, {formatTime(metrics.macro.rolling28.remaining)} remaining)
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* MESO - Today's FDP */}
          <div className="space-y-3">
            <h3 className="text-sm font-bold text-gray-300 uppercase tracking-wider flex items-center gap-2">
              <Plane className="w-4 h-4" />
              Meso - Today's Flight Duty Period
            </h3>
            <div className="space-y-2 pl-6">
              {/* Max FDP */}
              <div className="bg-white/5 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-400">Max FDP:</span>
                  <span className="text-sm font-semibold text-gray-300">
                    {formatTime(metrics.meso.maxFDP)} ({dutyPeriod?.legs?.length || 0} legs, {new Date(dutyPeriod?.report || Date.now()).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })}Z report)
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {getStatusIcon(metrics.meso.currentDuty, metrics.meso.maxFDP)}
                  <span className="text-sm">
                    Current: <span className={`font-bold ${getPercentColor((metrics.meso.currentDuty / metrics.meso.maxFDP) * 100)}`}>
                      {formatTime(metrics.meso.currentDuty)}
                    </span>
                    {' '}({Math.round((metrics.meso.currentDuty / metrics.meso.maxFDP) * 100)}% used, {formatTime(metrics.meso.maxFDP - metrics.meso.currentDuty)} remaining)
                  </span>
                </div>
              </div>

              {/* Max Flight */}
              <div className="bg-white/5 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-400">Max Flight Time:</span>
                  <span className="text-sm font-semibold text-gray-300">{formatTime(metrics.meso.maxFlight)}</span>
                </div>
                <div className="flex items-center gap-2">
                  {getStatusIcon(metrics.meso.currentFlight, metrics.meso.maxFlight)}
                  <span className="text-sm">
                    Current: <span className={`font-bold ${getPercentColor((metrics.meso.currentFlight / metrics.meso.maxFlight) * 100)}`}>
                      {formatTime(metrics.meso.currentFlight)}
                    </span>
                    {' '}({Math.round((metrics.meso.currentFlight / metrics.meso.maxFlight) * 100)}% used, {formatTime(metrics.meso.maxFlight - metrics.meso.currentFlight)} remaining)
                  </span>
                </div>
              </div>

              {/* Extension */}
              <div className="bg-blue-500/10 rounded-lg p-3 border border-blue-500/30">
                <span className="text-sm text-blue-400">
                  Extension Available: +{formatTime(metrics.meso.extensionAvailable)} (if needed)
                </span>
              </div>
            </div>
          </div>

          {/* MICRO - Individual Flights */}
          <div className="space-y-3">
            <h3 className="text-sm font-bold text-gray-300 uppercase tracking-wider">
              Micro - Individual Flights
            </h3>
            <div className="space-y-2 pl-6">
              {metrics.micro.map((leg, idx) => (
                <div key={idx} className="bg-white/5 rounded-lg p-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-semibold text-gray-500">Leg {idx + 1}</span>
                    <span className="text-sm font-mono text-gray-300">{leg.from}→{leg.to}</span>
                    {leg.flightNumber && (
                      <span className="text-xs text-gray-500">{leg.flightNumber}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-gray-300">{formatTime(leg.blockMinutes)} block</span>
                    {leg.status === 'completed' ? (
                      <CheckCircle className="w-4 h-4 text-green-500" />
                    ) : (
                      <Clock className="w-4 h-4 text-gray-500" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* REST REQUIREMENTS */}
          <div className="space-y-3">
            <h3 className="text-sm font-bold text-gray-300 uppercase tracking-wider flex items-center gap-2">
              <Moon className="w-4 h-4" />
              Rest Requirements
            </h3>
            <div className="space-y-2 pl-6">
              {/* Rest Before */}
              <div className="bg-white/5 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-400">Min Rest Before:</span>
                  <span className="text-sm font-semibold text-gray-300">{formatTime(metrics.rest.before.required)} required</span>
                </div>
                <div className="flex items-center gap-2">
                  {getStatusIcon(metrics.rest.before.actual, metrics.rest.before.required)}
                  <span className="text-sm">
                    Actual: <span className="font-bold text-green-500">{formatTime(metrics.rest.before.actual)}</span>
                  </span>
                </div>
              </div>

              {/* Rest After */}
              <div className="bg-white/5 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-400">Min Rest After:</span>
                  <span className="text-sm font-semibold text-gray-300">{formatTime(metrics.rest.after.required)} required</span>
                </div>
                <div className="flex items-center gap-2">
                  {getStatusIcon(metrics.rest.after.projected, metrics.rest.after.required)}
                  <span className="text-sm">
                    Projected: <span className="font-bold text-green-500">{formatTime(metrics.rest.after.projected)}</span>
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* CIRCADIAN */}
          <div className="space-y-3">
            <h3 className="text-sm font-bold text-gray-300 uppercase tracking-wider">
              Circadian Considerations
            </h3>
            <div className="pl-6">
              <div className={`rounded-lg p-3 ${metrics.circadian.isCircadianLow ? 'bg-yellow-500/10 border border-yellow-500/30' : 'bg-green-500/10 border border-green-500/30'}`}>
                <span className={`text-sm ${metrics.circadian.isCircadianLow ? 'text-yellow-400' : 'text-green-400'}`}>
                  Report Time: {metrics.circadian.reportHour.toString().padStart(2, '0')}:00Z
                  {metrics.circadian.isCircadianLow 
                    ? ' (in circadian low window - increased fatigue risk)'
                    : ' (not in circadian low window)'
                  }
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-white/10">
          <button
            onClick={onClose}
            className="w-full px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors"
          >
            Close Analysis
          </button>
        </div>
      </div>
    </div>
  );
};

export default FAR117DetailModal;
